import * as d3 from 'd3';

import { appendSelect } from 'd3-appendselect';
import last from 'lodash/last';
import merge from 'lodash/merge';

d3.selection.prototype.appendSelect = appendSelect;

const getAverage = (nums) => nums.reduce((a, b) => (a + b)) / nums.length;

const getRollingAvg = (array, lag = 7) => array
  .slice(lag - 1)
  .map((d, i) => array.slice(i, i + lag))
  .map((slice) => {
    if (slice.includes(null)) return null;
    const mean = getAverage(slice.map(d => d.count));
    return {
      date: last(slice).date,
      avg: mean < 0 ? 0 : mean,
    };
  });

class Chart {
  selection(selector) {
    if (!selector) return this._selection;
    this._selection = d3.select(selector);
    return this;
  }

  data(newData) {
    if (!newData) return this._data || this.defaultData;
    this._data = newData;
    return this;
  }

  props(newProps) {
    if (!newProps) return this._props || this.defaultProps;
    this._props = merge(this._props || this.defaultProps, newProps);
    return this;
  }

  defaultData = []

  defaultProps = {
    height: 225,
    margin: {
      top: 35,
      right: 40,
      bottom: 22,
      left: 40,
    },
    color: ['#ccc', '#fce587'],
  };

  draw() {
    const data = this.data();
    const props = this.props();
    const container = this.selection().node();
    const { width: containerWidth } = container.getBoundingClientRect();
    const width = containerWidth - props.margin.left - props.margin.right;
    const height = props.height - props.margin.top - props.margin.bottom;

    const { vaccinations, country, nationalCounts } = data;

    const rollingCases = getRollingAvg(nationalCounts.cases);
    const rollingDeaths = getRollingAvg(nationalCounts.deaths);

    const firstDate = vaccinations.peopleVaccinated[0].date;
    const peakCases = d3.max(rollingCases, d => d.avg);
    const peakDeaths = d3.max(rollingDeaths, d => d.avg);

    const casesData = rollingCases
      .filter(d => new Date(d.date) >= new Date(firstDate))
      .map(d => ({
        date: new Date(d.date),
        percent: d.avg / peakCases,
      }));

    const deathsData = rollingDeaths
      .filter(d => new Date(d.date) >= new Date(firstDate))
      .map(d => ({
        date: new Date(d.date),
        percent: d.avg / peakDeaths,
      }));
    
    const latestY = deathsData.slice(-1)[0].percent;

    const dateExtent = d3.extent(casesData, d => d.date);

    const xScale = d3.scaleLinear()
      .domain(dateExtent)
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([0, 1])
      .range([height, 0]);

    const xAxis = d3.axisBottom(xScale)
      .tickValues(xScale.domain())
      .tickPadding(5)
      .tickFormat(d3.timeFormat('%b. %e, %Y'));

    const yAxis = d3.axisLeft(yScale)
      .tickFormat(d3.format('.0%'))
      .tickValues([0, 0.5, 1]);

    const line = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.percent))
      .curve(d3.curveCardinal);

    const plot = this.selection()
      .appendSelect('svg')
      .attr('width', containerWidth)
      .attr('height', props.height)
      .appendSelect('g.plot')
      .attr('transform', `translate(${props.margin.left},${props.margin.top})`);

    plot.appendSelect('g.axis.x')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis);

    plot.appendSelect('g.axis.y')
      .call(yAxis);

    plot.appendSelect('path.cases')
      .datum(casesData)
      .style('fill', 'none')
      .style('stroke', props.color[0])
      .style('stroke-width', 1.75)
      .attr('d', line);

    plot.appendSelect('path.deaths')
      .datum(deathsData)
      .style('fill', 'none')
      .style('stroke', props.color[1])
      .style('stroke-width', 2.5)
      .attr('d', line);
    
    plot.appendSelect('text.data-label.deaths-label')
      .attr('x', width + 3)
      .attr('y', yScale(latestY) + 5)
      .style('fill', props.color[1])
      .text(`${Math.round(latestY * 1000) / 10}%`);

    const key = this.selection()
      .select('svg')
      .appendSelect('g.key');

    const offset = 135;

    key.appendSelect('text.legend')
      .attr('x', 0)
      .attr('y', 12)
      .text('Rolling 7-day average');

    key.appendSelect('line.cases')
      .attr('x1', 0 + offset)
      .attr('x2', 15 + offset)
      .attr('y1', 7)
      .attr('y2', 7)
      .style('stroke', props.color[0])
      .style('stroke-width', 1.75);

    key.appendSelect('text.cases')
      .attr('x', 20 + offset)
      .attr('y', 12)
      .style('fill', props.color[0])
      .text('Infections');

    key.appendSelect('line.deaths')
      .attr('x1', 0 + offset + 88)
      .attr('x2', 15 + offset + 88)
      .attr('y1', 7)
      .attr('y2', 7)
      .style('stroke', props.color[1])
      .style('stroke-width', 2.5);
    key.appendSelect('text.deaths')
      .attr('x', 20 + offset + 88)
      .attr('y', 12)
      .style('fill', props.color[1])
      .text('Deaths');

    return this; // Generally, always return the chart class from draw!
  }
}

export default Chart;
